import { AirValidationResponseSimple } from '@app/entities/air-validation.response';
import { SectorTripNumber } from '@user-history/entities/user-history-item';
import { HistoryBookingDetail, ReservationFlight } from '@user-history/entities/history-booking-detail';
import { PaymentReservationStatus } from '@user-history/entities/reservation-status';
import { environment as ENV } from '@environment';
import { LATAM_CODE } from '@shared/luggage/constants/airline-code.constants';
import { isConfirmReservationByStatus } from '@ux/mappers/reservation-status.mapper';

export enum ProvidersNotExcludedFromExchange {
  Amadeus = 'Amadeus'
}

export function mapSectorToValidation(reservationFlight: ReservationFlight): AirValidationResponseSimple {
  const { AirFareConditions, Sectors } = reservationFlight || {};
  return {
    TripOption: { Sectors },
    AirFareConditions
  };
}

export function getEnabledExchangeFlights(reservation: ReservationFlight, isMobile: boolean,
  paymentStatus: PaymentReservationStatus, inDateRange: boolean): boolean {
  return isMobile && (
    !!reservation && isConfirmReservationByStatus(reservation.Status) &&
    paymentPassValidation(paymentStatus) &&
    reservation?.IsExchangeImplemented
  ) && inDateRange && isNotDisabledByAirlinesOnExchange(reservation);
}

export function getEnabledExchangeFlightsByEtas(
  reservations: ReservationFlight[],
  isMobile: boolean,
  paymentStatus: PaymentReservationStatus
): boolean {
  const departureDate = getDepartureDate(reservations?.[0]);
  const arrivalDate = reservations.length > 1 ?
    getDepartureDate(reservations?.[reservations.length - 1]) :
    getArrivalDate(reservations?.[0]);
  const inDateRange = getInDateRange(departureDate, arrivalDate);
  const validateByAirline = reservations.length === 1 ?
    !!reservations.every(reservation => !!isNotDisabledByAirlinesOnExchange(reservation)) :
    !!reservations.some(reservation => !!isNotDisabledByAirlinesOnExchange(reservation));
  const validateIsExchangeImplemented = reservations.length === 1 ?
    !!reservations.every(reservation => !!reservation?.IsExchangeImplemented) :
    !!reservations.some(reservation => !!reservation?.IsExchangeImplemented);
  return isMobile && (
    !!reservations.every(reservation => isConfirmReservationByStatus(reservation.Status)) &&
    !!paymentPassValidation(paymentStatus) &&
    !!validateIsExchangeImplemented
  ) && !!inDateRange && !!validateByAirline;
}

function paymentPassValidation(paymentStatus: PaymentReservationStatus): boolean {
  return paymentStatus === PaymentReservationStatus.Approved || paymentStatus === PaymentReservationStatus.ParciallyApproved;
}

export function getArrivalAirportCode(reservation: ReservationFlight): string {
  const sectors = reservation?.Sectors ? reservation.Sectors : [];
  const departure = sectors.find(sector => sector.SectorNumber === SectorTripNumber.DEPARTURE);
  if (departure) {
    const flights = departure.Flights;
    return departure.Flights[flights.length - 1].ArrivalAirport;
  } else return null;
}


export function getDepartureDate(reservation: ReservationFlight): Date {
  const sectors = reservation?.Sectors ? reservation.Sectors : [];
  const departure = sectors.find(sector => sector.SectorNumber === SectorTripNumber.DEPARTURE);
  return departure ? new Date(departure.Flights[0].DepartureDate) : null;
}

export function getArrivalDate(reservation: ReservationFlight): Date {
  const sectors = reservation?.Sectors ? reservation.Sectors : [];
  const arrival = sectors.find(sector => sector.SectorNumber === SectorTripNumber.RETURN);
  return arrival ? new Date(arrival.Flights[0].DepartureDate) : null;
}

export function getInDateRange(departureDate: Date, arrivalDate: Date): boolean {
  return arrivalDate ? departureDate > new Date() || arrivalDate > new Date() :
    departureDate ? departureDate > new Date() : false;
}

export function getFlightReservation(detail: HistoryBookingDetail): ReservationFlight {
  if (!detail?.Reservations) {
    return {} as ReservationFlight;
  }
  const filtered = detail.Reservations.filter(r => r.Status === detail.Status) as ReservationFlight[];
  return filtered.length > 0 ? filtered[filtered.length - 1] : {} as ReservationFlight;
}

function isNotDisabledByAirlinesOnExchange(reservation: ReservationFlight): boolean {
  return !!reservation && !!reservation.Sectors && reservation.Sectors.every(sector => !!sector.Flights &&
    sector.Flights.every(flight => isNotDisabledByProviderOnExchange(flight.AirlineCode,
      reservation.BookingSource as ProvidersNotExcludedFromExchange)));
}

function isNotDisabledByProviderOnExchange(
  airline: string, source: ProvidersNotExcludedFromExchange): boolean {
  const disabledAirlines = ENV.countryModules?.user_history?.disabled_airlines_exchange || [];
  const isNotDisabled = !disabledAirlines.includes(airline);
  switch (airline) {
    case LATAM_CODE:
      return isNotDisabled || (!isNotDisabled && source === ProvidersNotExcludedFromExchange.Amadeus);
    default:
      return isNotDisabled;
  }
}
